import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

const TOAST_TYPES = {
  success: { bg: '#E8F5E9', border: '#27AE60', color: '#2E7D32', icon: '\u2713' },
  error:   { bg: '#FFEBEE', border: '#E74C3C', color: '#C62828', icon: '\u2717' },
  warning: { bg: '#FFF8E1', border: '#F39C12', color: '#E65100', icon: '\u26A0' },
  info:    { bg: '#E3F2FD', border: '#2E75B6', color: '#1565C0', icon: '\u2139' },
};

function ToastItem({ toast, onClose }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);
  const style = TOAST_TYPES[toast.type] || TOAST_TYPES.info;

  useEffect(() => {
    if (toast.duration !== 0) {
      timerRef.current = setTimeout(() => {
        setExiting(true);
        setTimeout(() => onClose(toast.id), 300);
      }, toast.duration || 4000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [toast.id, toast.duration, onClose]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 18px', borderRadius: 10,
      background: style.bg, borderLeft: `4px solid ${style.border}`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      minWidth: 320, maxWidth: 480,
      opacity: exiting ? 0 : 1,
      transform: exiting ? 'translateX(40px)' : 'translateX(0)',
      transition: 'all 0.3s ease',
      animation: 'toastSlideIn 0.3s ease',
    }}>
      <span style={{ fontSize: 18, lineHeight: '22px', color: style.color, flexShrink: 0 }}>{style.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div style={{ fontWeight: 600, fontSize: 14, color: style.color, marginBottom: 2 }}>{toast.title}</div>
        )}
        <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }}>{toast.message}</div>
      </div>
      <button onClick={handleClose} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: '#999',
        fontSize: 16, padding: '0 0 0 8px', lineHeight: 1, flexShrink: 0,
      }}>{'\u2715'}</button>
    </div>
  );
}

function ConfirmDialog({ confirm, onResolve }) {
  if (!confirm) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10001, animation: 'toastFadeIn 0.2s ease',
    }} onClick={() => onResolve(false)}>
      <div style={{
        background: 'white', borderRadius: 12, padding: '28px 32px', maxWidth: 440, width: '90%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)', animation: 'toastScaleIn 0.2s ease',
      }} onClick={e => e.stopPropagation()}>
        {confirm.title && (
          <h3 style={{ margin: '0 0 12px', fontSize: 17, color: '#333' }}>{confirm.title}</h3>
        )}
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
          {confirm.message}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={() => onResolve(false)} className="btn btn-outline"
            style={{ fontSize: 13, padding: '8px 20px' }}>
            {confirm.cancelLabel || 'Annuler'}
          </button>
          <button onClick={() => onResolve(true)}
            className={`btn ${confirm.danger ? 'btn-primary' : 'btn-accent'}`}
            style={{ fontSize: 13, padding: '8px 20px',
              background: confirm.danger ? '#E74C3C' : undefined }}>
            {confirm.confirmLabel || 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const resolveRef = useRef(null);
  const idRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, options = {}) => {
    const id = ++idRef.current;
    const toast = {
      id,
      message,
      type: options.type || 'info',
      title: options.title || null,
      duration: options.duration !== undefined ? options.duration : 4000,
    };
    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  const success = useCallback((message, options = {}) => addToast(message, { ...options, type: 'success' }), [addToast]);
  const error = useCallback((message, options = {}) => addToast(message, { ...options, type: 'error' }), [addToast]);
  const warning = useCallback((message, options = {}) => addToast(message, { ...options, type: 'warning' }), [addToast]);
  const info = useCallback((message, options = {}) => addToast(message, { ...options, type: 'info' }), [addToast]);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirm({ message, ...options });
    });
  }, []);

  const handleConfirmResolve = useCallback((result) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setConfirm(null);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, success, error, warning, info, confirm: showConfirm }}>
      {children}
      {/* Toast container */}
      <div style={{
        position: 'fixed', top: 20, right: 20, zIndex: 10000,
        display: 'flex', flexDirection: 'column', gap: 10,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <ToastItem toast={t} onClose={removeToast} />
          </div>
        ))}
      </div>
      {/* Confirm dialog */}
      <ConfirmDialog confirm={confirm} onResolve={handleConfirmResolve} />
      {/* Animations */}
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes toastFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes toastScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
